import { useState, useCallback, useRef } from 'react';
import axios from '../utils/api';
import notify from '../utils/notify';
import { callWithRetry } from '../utils/retry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANG_OPTIONS = [
  { id: 'EN', label: 'EN', srLang: 'en-US' },
  { id: 'HI', label: '\u0939\u093F', srLang: 'hi-IN' },
];

export { LANG_OPTIONS };

export const useVoiceToText = ({ onTranscript }) => {
  const [isListening, setIsListening] = useState(false);
  const [selectedLang, setSelectedLang] = useState('EN');
  const [voiceRephrasing, setVoiceRephrasing] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { notify.error('Speech recognition is not supported in this browser. Please use Chrome.'); return; }
    const recognition = new SpeechRecognition();
    const langOpt = LANG_OPTIONS.find(l => l.id === selectedLang) || LANG_OPTIONS[0];
    recognition.lang = langOpt.srLang;
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = async (event) => {
      const transcript = event.results[0][0].transcript;
      setIsListening(false);
      setVoiceRephrasing(true);
      try {
        // Auto-retry once on flaky AI provider failures; if both attempts
        // fail, fall back to the raw transcript so the user's voice input
        // isn't lost.
        const res = await callWithRetry(() => axios.post(`${BACKEND_URL}/api/voice-rephrase`, { transcript, language: selectedLang }));
        onTranscript(res.data.text);
      } catch {
        onTranscript(transcript);
      } finally { setVoiceRephrasing(false); }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, [selectedLang, onTranscript]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    isListening ? stopListening() : startListening();
  }, [isListening, startListening, stopListening]);

  return { isListening, selectedLang, setSelectedLang, voiceRephrasing, toggleListening };
};
